import type { ParallaxSvgSpec } from '../../../../config/atmosphere/effectLayer.types';

/** Small, renderer-independent scenery vocabulary. Coordinates are 0..100;
 * placements are compiled into SVG strings once, never in the render loop. */
export const WORLD_SHAPES = {
  oak: 'M43 100L46 62C16 70 2 48 12 35C4 18 28 8 39 14C49 0 77 7 78 21C99 20 100 49 84 54C76 68 59 63 56 62L60 100Z',
  fir: 'M43 100L43 84L8 84L29 60L16 60L38 33L27 33L50 0L74 33L62 33L86 60L73 60L96 84L57 84L57 100Z',
  // An uneven, spreading crown with two short branch shoulders. Autumn reads
  // through crown clumps and palette, rather than a literal maple-leaf outline.
  maple: 'M42 100L45 76L35 61C18 72 4 63 7 48C1 29 13 16 28 22C24 9 44 1 53 12C67 1 82 8 81 24C99 24 101 41 89 49C99 63 80 73 65 63L53 78L58 100Z',
  acacia: 'M44 100L47 48L29 35L6 35C0 20 20 18 30 20C34 5 62 7 70 18C94 13 100 29 94 34L68 37L56 49L60 100Z',
  blossom: 'M36 100L44 62L26 47C7 54 0 38 8 30C2 13 25 9 33 19C38 2 57 3 65 14C83 4 99 19 89 32C104 46 79 57 61 46L51 64L54 100Z',
  bamboo: 'M39 100L41 58L12 45L34 47L42 50L44 24L22 10L43 16L44 0L51 0L50 31L75 16L91 15L69 33L50 40L49 70L71 55L91 56L69 73L49 78L48 100Z',
  palm: 'M43 100Q58 61 51 29Q20 21 0 37Q9 6 46 19Q20 1 16 0Q46 1 56 16Q71 0 93 5Q71 13 64 25Q88 9 100 38Q78 25 63 32Q69 65 58 100Z',
  willow: 'M43 100L45 46Q16 43 9 72Q0 18 36 18Q52 0 76 21Q100 24 94 77Q83 43 58 43L60 100Z',
  snag: 'M43 100L46 62L20 40L12 13L23 33L40 43L37 7L49 38L56 0L61 5L57 44L78 24L88 25L66 53L55 66L62 100Z',
  reeds: 'M15 100L8 36L22 79L28 12L34 69L47 35L39 88L54 70L62 2L65 79L87 28L76 82L97 59L84 100Z',
  mushroom: 'M36 100Q48 66 42 44L2 47C-2 23 29 0 52 4C77 2 99 27 100 46L58 45Q56 77 71 100Z',
  crystal: 'M16 100L3 45L20 28L39 68L36 19L58 0L75 26L69 69L91 46L100 71L88 100Z',
  mesa: 'M0 100L12 78L18 37L26 31L29 10L67 10L70 25L80 27L86 72L100 100Z',
  mountain: 'M0 100L13 75L26 63L48 3L58 22L62 21L81 64L100 100Z',
  volcano: 'M0 100L30 45L38 14L49 23L59 13L71 50L100 100Z',
  basalt: 'M0 100L0 39L14 34L14 15L34 8L48 15L48 48L62 39L62 20L80 12L100 24L100 100Z',
  ruin: 'M4 100L4 19L20 19L20 4L54 4L54 14L80 14L80 29L96 29L96 100L79 100L79 48L63 32L40 32L22 48L22 100Z',
  column: 'M17 100L17 88L29 88L34 19L22 19L22 5L80 5L80 19L68 19L73 88L85 88L85 100Z',
  obelisk: 'M24 100L35 16L54 0L69 18L78 100Z',
  gable: 'M10 100L10 46L0 46L41 9L54 22L54 3L64 3L64 31L100 61L88 61L88 100Z',
  island: 'M0 28L12 18L36 22L51 6L73 15L94 11L100 28L80 43L70 68L53 58L44 100L30 62L15 51Z',
  ribs: 'M8 100Q-8 36 40 6L46 16Q11 48 23 100Z M77 100Q87 48 54 16L60 6Q108 36 92 100Z',
} as const;

export type WorldShape = keyof typeof WORLD_SHAPES;
export interface WorldBackdrop { far: ParallaxSvgSpec; mid: ParallaxSvgSpec; near: ParallaxSvgSpec; }

/** Absolute paired commands only (M/L/Q/C/Z) keep placement simple and exact.
 * The anchor is the foot of the shape, matching terrain silhouette baselines. */
export function placeWorldShape(shape: WorldShape, x: number, ground: number, width: number, height: number): string {
  let coordinate = 0;
  return WORLD_SHAPES[shape].replace(/-?\d+(?:\.\d+)?/g, token => {
    const value = Number(token);
    const placed = coordinate++ % 2 === 0 ? x + value * width / 100 : ground - height + value * height / 100;
    return String(Math.round(placed * 100) / 100);
  });
}

/** Authored 2400×400 repeat: all terrain profiles meet at y=300 on both edges.
 * Sparse asymmetric groupings avoid a picket-fence rhythm at gameplay scale. */
export const WORLD_PROFILES = {
  hills: 'M0 400L0 300C150 300 210 235 390 228C590 218 660 303 850 305C1090 308 1170 212 1400 226C1620 240 1660 308 1880 303C2110 298 2230 300 2400 300L2400 400Z',
  banks: 'M0 400L0 300Q130 300 260 282L470 282Q610 282 720 313L950 313Q1110 313 1270 294L1500 294Q1640 294 1820 315L2070 315Q2250 300 2400 300L2400 400Z',
  dunes: 'M0 400L0 300C210 300 480 105 720 162C916 206 983 305 1200 300C1450 292 1710 171 1870 216C2090 270 2230 300 2400 300L2400 400Z',
  crags: 'M0 400L0 300L180 300L230 266L400 254L530 286L700 281L820 215L1010 196L1140 262L1320 310L1540 290L1600 230L1830 245L2010 298L2200 289L2400 300L2400 400Z',
  shelf: 'M0 400L0 300L210 300L240 287L520 287L540 300L990 300L1020 281L1390 281L1430 300L1900 300L1940 289L2210 289L2250 300L2400 300L2400 400Z',
} as const;

export type ShapePlacement = readonly [WorldShape, number, number, number, number];
export function sceneryTile(profile: keyof typeof WORLD_PROFILES | null, placements: readonly ShapePlacement[]): ParallaxSvgSpec {
  return { tile: { viewBoxWidth: 2400, viewBoxHeight: 400,
    path: [profile ? WORLD_PROFILES[profile] : '', ...placements.map(p => placeWorldShape(...p))].filter(Boolean).join(' '),
  } };
}
